import React, { useEffect, useState, useCallback } from 'react';
import { formatDateTime } from '../../utils/datetime';

interface Report {
 report_hash: string;
 listing_hash: string | null;
 proposal_hash: string | null;
 reporter_mopeer: string;
 reported_mopeer: string | null;
 reason: string;
 status: 'pending' | 'reviewing' | 'resolved' | 'dismissed';
 admin_note: string | null;
 created_at: string;
 resolved_at: string | null;
}

const STATUS_LABEL: Record<string, string> = {
 pending: '대기', reviewing: '검토 중', resolved: '조치', dismissed: '기각',
};

const MarketAdmin: React.FC = () => {
 const [reports, setReports] = useState<Report[]>([]);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);

 const load = useCallback(() => {
  setLoading(true);
  fetch('/market/reports/admin', { credentials: 'include' })
   .then(r => {
    if (r.status === 403) throw new Error('관리자 권한 필요');
    return r.json();
   })
   .then(j => setReports(j.success ? j.data : []))
   .catch(err => setError(err.message))
   .finally(() => setLoading(false));
 }, []);

 useEffect(() => { load(); }, [load]);

 const resolve = async (hash: string, decision: 'resolved' | 'dismissed') => {
  const note = prompt(`${decision === 'resolved' ? '조치' : '기각'} 사유 (메모):`, '') || '';
  const r = await fetch(`/market/reports/admin/${hash}/resolve`, {
   method: 'POST', headers: { 'Content-Type': 'application/json' },
   credentials: 'include', body: JSON.stringify({ decision, note }),
  });
  const j = await r.json();
  if (!r.ok || !j.success) { alert(j.error || '실패'); return; }
  load();
 };

 if (error) return <div className="bg-red-50 text-red-700 p-3">{error}</div>;

 return (
  <div>
   <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">마켓 신고 관리</h1>
   {loading && <div className="text-gray-500 text-sm py-8 text-center">로딩 중...</div>}
   {!loading && reports.length === 0 && (
    <div className="text-gray-400 text-sm py-12 text-center">신고 없음.</div>
   )}
   <div className="space-y-2">
    {reports.map(r => (
     <div key={r.report_hash} className="bg-white border border-gray-200 p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
       <div className="text-xs text-gray-500">
        {formatDateTime(r.created_at)} ·
        {r.listing_hash && <span className="ml-1">listing: <a href={`/market/${r.listing_hash}`} className="text-blue-600">{r.listing_hash.slice(0, 16)}…</a></span>}
        {r.proposal_hash && <span className="ml-1">proposal: <a href={`/market/proposals/${r.proposal_hash}`} className="text-blue-600">{r.proposal_hash.slice(0, 16)}…</a></span>}
       </div>
       <span className="text-[10px] px-1.5 py-0.5 bg-gray-100">{STATUS_LABEL[r.status]}</span>
      </div>
      <div className="text-xs text-gray-500 mb-1">
       신고자: <span className="font-mono">{r.reporter_mopeer?.slice(0, 16)}…</span>
       {r.reported_mopeer && <> · 피신고자: <span className="font-mono">{r.reported_mopeer.slice(0, 16)}…</span></>}
      </div>
      <div className="text-sm whitespace-pre-wrap mb-2">{r.reason}</div>
      {r.admin_note && (
       <div className="text-xs text-gray-600 bg-gray-50 p-2 mb-2">관리자 메모: {r.admin_note}</div>
      )}
      {r.status === 'pending' && (
       <div className="flex gap-2">
        <button onClick={() => resolve(r.report_hash, 'resolved')}
         className="text-xs px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white">조치</button>
        <button onClick={() => resolve(r.report_hash, 'dismissed')}
         className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300">기각</button>
       </div>
      )}
     </div>
    ))}
   </div>
  </div>
 );
};

export default MarketAdmin;
